import React from 'react';
import { Icon } from '@iconify/react';
import { Link } from '../../components/common/NextAdapters';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePendingMovies, usePendingEpisodes, useContentCreators } from '../../hooks/useMovie';

const SECTIONS = [
  {
    label: 'Content',
    tiles: [
      { to: '/admin/movies', icon: 'ph:film-slate-bold', label: 'Pending Movies', hint: 'Review pending uploads' },
      { to: '/admin/episodes', icon: 'ph:play-circle-bold', label: 'Pending Episodes', hint: 'Review pending uploads' },
      { to: '/admin/catalog', icon: 'ph:stack-bold', label: 'Catalog & Cast', hint: 'Browse and edit titles' },
      { to: '/admin/categories', icon: 'ph:folder-bold', label: 'Categories', hint: 'Organize genres' },
      { to: '/admin/actors', icon: 'ph:identification-badge-bold', label: 'Actors', hint: 'Manage cast records' },
    ],
  },
  {
    label: 'Monetization',
    tiles: [
      { to: '/admin/plans', icon: 'ph:credit-card-bold', label: 'Subscription Plans', hint: 'Pricing & tiers' },
      { to: '/admin/subscriptions', icon: 'ph:receipt-bold', label: 'Subscriptions', hint: 'Active subscribers' },
      { to: '/admin/payouts', icon: 'ph:currency-dollar-bold', label: 'Payouts', hint: 'Creator payout runs' },
      { to: '/admin/payouts/config', icon: 'ph:sliders-horizontal-bold', label: 'Payout Config', hint: 'Rates & rules' },
    ],
  },
  {
    label: 'People',
    tiles: [
      { to: '/admin/users', icon: 'ph:users-bold', label: 'Users', hint: 'Accounts & roles' },
      { to: '/admin/content-creators', icon: 'ph:star-bold', label: 'Content Creators', hint: 'Upload access requests' },
    ],
  },
  {
    label: 'System',
    tiles: [{ to: '/admin/config', icon: 'ph:gear-six-bold', label: 'System Config', hint: 'Platform settings' }],
  },
];

function AlertCard({ icon, count, noun, to }) {
  if (count <= 0) return null;
  return (
    <Link
      href={to}
      className="group flex items-center gap-3 rounded-xl border border-[#ff7a00]/25 bg-[#ff7a00]/[0.07] px-4 py-3 text-sm transition-colors hover:bg-[#ff7a00]/[0.12]"
    >
      <Icon icon={icon} className="size-4 shrink-0 text-[#ff7a00]" />
      <span className="text-foreground/90">
        <span className="font-semibold text-[#ff7a00]">{count}</span> {noun}
        {count > 1 ? 's' : ''} awaiting approval
      </span>
      <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-[#ff7a00]">
        Review
        <Icon icon="ph:arrow-right-bold" className="size-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

export default function AdminDashboardPage() {
  usePageTitle('Admin Dashboard · Kast');
  const { data: pendingMovies = [] } = usePendingMovies();
  const { data: pendingEpisodes = [] } = usePendingEpisodes();
  const { data: creators = [] } = useContentCreators();
  const creatorsAwaitingApproval = creators.filter((c) => !c.CanUploadContent);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      {(pendingMovies.length > 0 || pendingEpisodes.length > 0 || creatorsAwaitingApproval.length > 0) && (
        <div className="mb-8 flex flex-col gap-2.5">
          <AlertCard icon="ph:film-slate-bold" count={pendingMovies.length} noun="movie" to="/admin/movies" />
          <AlertCard icon="ph:play-circle-bold" count={pendingEpisodes.length} noun="episode" to="/admin/episodes" />
          <AlertCard
            icon="ph:star-bold"
            count={creatorsAwaitingApproval.length}
            noun="content creator"
            to="/admin/content-creators"
          />
        </div>
      )}

      <div className="flex flex-col gap-7">
        {SECTIONS.map((section) => (
          <div key={section.label}>
            <h2 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
              {section.label}
            </h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {section.tiles.map((tile) => (
                <Link
                  key={tile.to}
                  href={tile.to}
                  className="group flex items-center gap-3 rounded-lg px-3.5 py-3 ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.04] hover:ring-foreground/15"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-white/5 text-muted-foreground/80 transition-colors group-hover:text-foreground">
                    <Icon icon={tile.icon} className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground/90">{tile.label}</span>
                    <span className="block truncate text-xs text-muted-foreground/60">{tile.hint}</span>
                  </span>
                  <Icon
                    icon="ph:arrow-right-bold"
                    className="size-3.5 shrink-0 text-muted-foreground/30 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground"
                  />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
